import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// API 기본 URL 설정
const API_BASE_URL = 'https://att-api.bs-soft.co.kr/api';

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 로컬 스토리지에서 토큰 가져오기
  const getToken = () => localStorage.getItem('token');

  // 인증 상태 확인
  const isAuthenticated = !!getToken() && !!user;

  // 로그인 함수
  const login = async (username, password) => {
    try {
      setError(null);
      console.log('로그인 API 요청 시작:', `${API_BASE_URL}/auth/login`);
      
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password
      });

      const { access_token, user: userData } = response.data;
      console.log('로그인 성공, 토큰 발급됨');
      console.log('서버에서 받은 사용자 정보:', userData);
      
      // 토큰 저장
      localStorage.setItem('token', access_token);
      
      // 서버에서 직접 받은 사용자 정보가 있으면 먼저 설정
      if (userData) {
        setUser(userData);
        console.log('서버에서 받은 사용자 정보로 설정됨:', userData);
      }
      
      // 추가적인 회원 정보 가져오기
      await fetchUserInfo();
      
      // 회원 정보가 제대로 로드되었는지 확인
      if (!user) {
        console.error('회원 정보를 가져왔지만 회원 객체가 없습니다.');
        setError('회원 정보를 불러오는데 실패했습니다.');
        return false;
      }
      
      console.log('로그인 완료, 회원 정보 로드됨:', user);
      return true;
    } catch (err) {
      console.error('로그인 오류:', err);
      
      // 자세한 오류 정보 로깅
      if (err.response) {
        console.error('API 응답 상태:', err.response.status);
        console.error('API 응답 데이터:', err.response.data);
        
        // 오류 메시지 설정
        const errorMessage = err.response?.data?.message || '로그인에 실패했습니다.';
        setError(errorMessage);
        
        if (err.response.status === 401) {
          return { success: false, message: '아이디 또는 비밀번호가 올바르지 않습니다.' };
        }
      } else {
        setError('서버 연결에 실패했습니다.');
      }
      
      return false;
    }
  };

  // 로그아웃 함수
  const logout = () => {
    try {
      // 로컬 스토리지에서 토큰 제거
      localStorage.removeItem('token');
      // 회원 상태 초기화
      setUser(null);
      // 에러 상태 초기화
      setError(null);
      
      console.log('로그아웃 성공');
      return true;
    } catch (err) {
      console.error('로그아웃 중 오류 발생:', err);
      return false;
    }
  };

  // 회원 정보 가져오기
  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      if (!token) {
        setUser(null);
        console.log('토큰이 없습니다. 회원 정보를 초기화합니다.');
        return;
      }
      
      console.log('프로필 정보 요청 시작:', `${API_BASE_URL}/auth/profile`);
      const response = await axios.get(`${API_BASE_URL}/auth/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const userData = response.data;
      console.log('서버에서 받은 response:', response);
      console.log('서버에서 받은 회원 정보:', userData);
      
      // JWT 토큰 복호화 정보 확인 (roles 정보가 있을 수 있음)
      if (!userData.roles) {
        try {
          // JWT 토큰 body를 디코딩하여 사용자 정보 추출 (토큰의 payload 부분)
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const tokenPayload = JSON.parse(atob(tokenParts[1]));
            console.log('JWT 토큰에서 추출한 정보:', tokenPayload);
            
            // 토큰에 역할 정보가 있는 경우
            if (tokenPayload.roles) {
              userData.roles = Array.isArray(tokenPayload.roles) 
                ? tokenPayload.roles 
                : [tokenPayload.roles];
              console.log('JWT 토큰에서 역할 정보를 추출했습니다:', userData.roles);
            }
          }
        } catch (tokenErr) {
          console.error('JWT 토큰 처리 중 오류:', tokenErr);
        }
      }
      
      // 역할 정보가 여전히 없는 경우 기본값 설정
      if (!userData.roles) {
        console.warn('역할 정보를 찾을 수 없습니다. 기본 역할을 설정합니다.');
        // 역할은 admin과 instructor 두 가지만 존재
        if (userData.username === 'admin') {
          userData.roles = ['admin'];
        } else {
          userData.roles = ['instructor']; // 기본적으로 강사 역할 할당
        }
      }
      
      // 역할 정보 형식 확인 및 변환
      if (userData.roles && !Array.isArray(userData.roles)) {
        console.warn('역할 정보가 배열 형식이 아닙니다:', userData.roles);
        userData.roles = [userData.roles];
      }
      
      console.log('가공된 회원 정보:', userData);
      setUser(userData);
      console.log('인증 상태 업데이트:', { isAuthenticated: !!token, user: userData });
    } catch (err) {
      console.error('회원 정보를 가져오는데 실패했습니다:', err);
      
      // API 응답 상세 정보 로깅
      if (err.response) {
        console.error('API 응답 상태:', err.response.status);
        console.error('API 응답 데이터:', err.response.data);
      }
      
      // 토큰이 유효하지 않은 경우 로그아웃
      if (err.response?.status === 401 || err.response?.status === 404) {
        console.log('인증 오류로 인해 로그아웃합니다.');
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  // 앱 로드 시 회원 정보 확인
  useEffect(() => {
    fetchUserInfo();
  }, []);

  // API 요청을 위한 Axios 인스턴스 생성
  const authAxios = axios.create({
    baseURL: API_BASE_URL
  });
  
  // 요청 인터셉터 설정
  authAxios.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // 응답 인터셉터 설정
  authAxios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    authAxios
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 